/**
 * Data layer for `advertiser-invoices`. Thin typed fetch wrappers over
 * /api/v1/advertiser-invoices. Raw `fetch()` is confined to this file per the
 * dash contract (D6).
 *
 * Backend surface (apps/api/src/routes/advertiser-invoices.ts):
 *   GET  /:advertiser_id?year=YYYY  -> list + footer totals
 *   GET  /one/:id                   -> single invoice
 *   GET  /:id/pdf                   -> { id, url }
 *   POST /generate/:advertiser_id   -> idempotent draft create
 */

export type InvoiceStatus = "draft" | "issued" | "paid";

export interface AdvertiserInvoiceRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	period_start: number;
	period_end: number;
	subtotal_cents: number;
	tax_cents: number;
	total_cents: number;
	currency: string;
	status: InvoiceStatus;
	issued_at: number;
	pdf_url: string;
	created_at: number;
	updated_at: number;
}

export interface FooterTotals {
	count: number;
	subtotal_cents: number;
	tax_cents: number;
	total_cents: number;
	currency: string;
}

export interface ListAdvertiserInvoicesResponse {
	advertiser_id: string;
	year: number | null;
	range: { start: number; end: number } | null;
	count: number;
	totals: FooterTotals;
	invoices: AdvertiserInvoiceRow[];
}

export interface InvoicePdfResponse {
	id: string;
	url: string;
}

const BASE = "/api/v1/advertiser-invoices";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export interface ListParams {
	advertiserId: string;
	year?: number;
}

export async function list(params: ListParams): Promise<ListAdvertiserInvoicesResponse> {
	const qs = params.year ? `?year=${encodeURIComponent(String(params.year))}` : "";
	const res = await fetch(`${BASE}/${encodeURIComponent(params.advertiserId)}${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAdvertiserInvoicesResponse>(res);
}

export async function getPdfUrl(id: string): Promise<InvoicePdfResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}/pdf`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<InvoicePdfResponse>(res);
}

export const advertiserInvoicesQueryKeys = {
	all: ["advertiser-invoices"] as const,
	list: (advertiserId: string, year: number | undefined) =>
		[...advertiserInvoicesQueryKeys.all, "list", advertiserId, year ?? null] as const,
	detail: (id: string) => [...advertiserInvoicesQueryKeys.all, id] as const,
	pdf: (id: string) => [...advertiserInvoicesQueryKeys.all, id, "pdf"] as const,
};
